// Editing a resource's own details.
//
// Until now these were CREATE-ONLY on the web: `PATCH /api/resources/:id` has
// always accepted name, kind, category, notes and the linked account, and
// nothing in the app ever sent them. A person whose name was typed wrong had
// to be deleted and re-created, which takes their bookings and their history
// with them — so the workaround was worse than the gap.
//
// Notes is the one field here that had NO way in at all, from any screen.
//
// Status is deliberately NOT here. It stays as the three buttons on the
// Overview panel: it is a frequent one-click action with booking consequences
// of its own, and burying it two clicks deep inside a form about spelling
// would be a worse trade than the small inconsistency of it living elsewhere.
import { useState } from 'react';
import { ApiError } from '../../api';
import { scheduling, type Resource, type ResourceKind } from '../../schedulingApi';
import type { MachineType } from '../../fleetApi';
import type { User } from '../../api';
import { KIND_LABEL } from '../../lib/resourceGroups';
import { Modal } from '../ui';
import { Field } from '../../ui';

/** Every kind, so a mis-typed resource can be moved rather than re-created. */
const KINDS: ResourceKind[] = [
  'person',
  'plant',
  'vehicle',
  'subcontractor',
  'supplier',
  'other',
];

export function EditResourceModal({
  resource,
  users,
  /**
   * False when the user list could not be loaded — listing users is
   * admin-only, so a manager gets no picker. See the note on the payload
   * below: this is not cosmetic.
   */
  canLinkAccounts,
  /**
   * Null when the list could not be loaded. Same rule as the account picker:
   * a form that cannot show a field OMITS it rather than sending its empty
   * value — a blank select here would silently strip the machine's type
   * while saving a spelling fix.
   */
  machineTypes,
  onClose,
  onSaved,
}: {
  resource: Resource;
  users: User[];
  canLinkAccounts: boolean;
  machineTypes: MachineType[] | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [name, setName] = useState(resource.name);
  const [kind, setKind] = useState<ResourceKind>(resource.kind);
  const [category, setCategory] = useState(resource.category ?? '');
  const [notes, setNotes] = useState(resource.notes ?? '');
  const [linkedUserId, setLinkedUserId] = useState(resource.linkedUserId ?? '');
  const [identifier, setIdentifier] = useState(resource.identifier ?? '');
  const [registrationNumber, setRegistrationNumber] = useState(resource.registrationNumber ?? '');
  const [registrationExpiry, setRegistrationExpiry] = useState(resource.registrationExpiry ?? '');
  const [machineTypeId, setMachineTypeId] = useState(resource.machineTypeId ?? '');
  const [dateOfBirth, setDateOfBirth] = useState(resource.dateOfBirth ?? '');
  const [employmentStart, setEmploymentStart] = useState(resource.employmentStartDate ?? '');
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const isAsset = kind === 'plant' || kind === 'vehicle';
  // Follows the form's own kind picker, not the saved row, so changing the
  // kind relabels the field before the save rather than after it.
  const isPerson = kind === 'person';
  const kindChanged = kind !== resource.kind;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFields({});

    try {
      const result = await scheduling.updateResource(resource.id, {
        name,
        kind,
        category: category === '' ? null : category,
        notes: notes === '' ? null : notes,

        // Sent only when this person could actually see the picker. A manager
        // gets an empty list (listing users is admin-only), so the select
        // would sit on "Not linked" and sending that would silently unlink an
        // account they were never shown. Omitting the field entirely is the
        // difference between "set it to nothing" and "leave it alone" — the
        // PATCH treats undefined as unchanged.
        ...(canLinkAccounts
          ? {
              linkedUserId: kind === 'person' && linkedUserId !== '' ? linkedUserId : null,
            }
          : {}),

        // The fleet fields, sent only on the kinds that hold them. The server
        // nulls the rest on a kind change anyway; this is so the request says
        // what the form showed.
        ...(isAsset
          ? {
              identifier: identifier === '' ? null : identifier,
              registrationNumber: registrationNumber === '' ? null : registrationNumber,
              registrationExpiry: registrationExpiry === '' ? null : registrationExpiry,
              // Only when the picker was actually on screen (see the prop).
              ...(machineTypes !== null
                ? { machineTypeId: machineTypeId === '' ? null : machineTypeId }
                : {}),
            }
          : {}),

        // A person's own dates (round 37), sent only on a person — the same
        // rule as the fleet fields, so the request says what the form showed.
        ...(isPerson
          ? {
              dateOfBirth: dateOfBirth === '' ? null : dateOfBirth,
              employmentStartDate: employmentStart === '' ? null : employmentStart,
            }
          : {}),
      });

      // A kind or name change can leave bookings needing a look, exactly as a
      // status change can — so it is reported the same way rather than saved
      // silently.
      onSaved(
        result.affectedBookings.length > 0
          ? `Saved. ${result.affectedBookings.length} booking${
              result.affectedBookings.length === 1 ? '' : 's'
            } now need re-planning.`
          : 'Saved.'
      );
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields);
      } else {
        setError('Could not save those changes.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Edit ${resource.name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn-ghost btn-sm">
            Cancel
          </button>
          <button type="submit" form="edit-resource" disabled={busy} className="btn-primary btn-sm">
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && (
        <div className="banner-error mb-3" role="alert">
          {error}
        </div>
      )}

      {/* `legacy-page` scopes the transition layer to this form, the same way
          the add form does — both use the shared Field component's bare
          label/input markup. */}
      <form id="edit-resource" onSubmit={submit} noValidate className="legacy-page">
        <Field
          label="Name"
          name="name"
          value={name}
          onChange={setName}
          error={fields.name}
          hint="“Jack Miller”, or “5T Excavator (KOB-05)”."
        />

        <label htmlFor="edit-kind">
          Kind
          <select
            id="edit-kind"
            value={kind}
            onChange={(e) => setKind(e.target.value as ResourceKind)}
          >
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k]}
              </option>
            ))}
          </select>
          {fields.kind && <span className="field-error">{fields.kind}</span>}
          <span className="hint">
            {kindChanged
              ? 'Changing this clears anything the new kind cannot hold: a person’s linked ' +
                'account, a machine’s ID, registration and service details. Bookings, documents ' +
                'and history stay. A sale record is never cleared.'
              : 'Every kind, so something entered under the wrong one can be moved rather than ' +
                'deleted and re-created; deleting takes its bookings and history with it.'}
          </span>
        </label>

        {isAsset && (
          <>
            <label htmlFor="edit-identifier">
              ID
              <input
                id="edit-identifier"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="EX-05"
              />
              <span className="hint">
                Your own label for the machine; it shows next to the name wherever this appears.
              </span>
            </label>

            <label htmlFor="edit-registrationNumber">
              Registration
              <input
                id="edit-registrationNumber"
                value={registrationNumber}
                onChange={(e) => setRegistrationNumber(e.target.value)}
                placeholder="XT17AZ"
              />
            </label>

            <label htmlFor="edit-registrationExpiry">
              Registration expiry
              <input
                id="edit-registrationExpiry"
                type="date"
                value={registrationExpiry}
                onChange={(e) => setRegistrationExpiry(e.target.value)}
              />
              {fields.registrationExpiry && (
                <span className="field-error">{fields.registrationExpiry}</span>
              )}
            </label>

            {machineTypes !== null && (
              <label htmlFor="edit-machineTypeId">
                Machine type
                <select
                  id="edit-machineTypeId"
                  value={machineTypeId}
                  onChange={(e) => setMachineTypeId(e.target.value)}
                >
                  <option value="">No type</option>
                  {machineTypes
                    // Archived types are offered ONLY where already held: a
                    // re-save must not lose it, but nothing new gets one.
                    .filter((t) => !t.archived || t.id === resource.machineTypeId)
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                        {t.archived ? ' (archived)' : ''}
                      </option>
                    ))}
                </select>
                {fields.machineTypeId && (
                  <span className="field-error">{fields.machineTypeId}</span>
                )}
                <span className="hint">
                  From your Machine types list; it decides which forms this machine is offered
                  for.
                </span>
              </label>
            )}
          </>
        )}

        {/* "Position" on a person (round 37), and no `list=`/`<datalist>`:
            the browser draws a dropdown arrow for one whether or not it has
            anything to offer, so a field whose own hint says free text wore a
            control that promised a menu and often opened nothing. */}
        <label htmlFor="edit-category">
          {isPerson ? 'Position' : 'Category'}
          <input
            id="edit-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder={isPerson ? 'Excavator operator, Foreman…' : 'Excavator, Tipper…'}
          />
          <span className="hint">Free text, used for grouping and filtering.</span>
        </label>

        {/* A person's own dates (round 37). Both optional and both said to be
            optional: a company that records neither gets a product that never
            asks again, and the reminders simply never mention them. */}
        {isPerson && (
          <>
            {/* `pair`: side by side on a phone too, not a cramped wrap
                (legacy.css has the argument). */}
            <div className="row pair">
              <label htmlFor="edit-dob">
                Date of birth
                <input
                  id="edit-dob"
                  type="date"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                />
              </label>
              <label htmlFor="edit-started">
                Started
                <input
                  id="edit-started"
                  type="date"
                  value={employmentStart}
                  onChange={(e) => setEmploymentStart(e.target.value)}
                />
              </label>
            </div>
            {/* Under the row, never on one of the two fields: `.row` is
                items-end and a hint below one input pushes that field out of
                line with its neighbour (round 34's rule). */}
            <p className="hint -mt-1">
              Optional. Used only for birthday and work-anniversary reminders, which your managers
              see, never the age, and never anyone outside your company.
            </p>
          </>
        )}

        <label htmlFor="edit-notes">
          Notes
          <textarea
            id="edit-notes"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          {fields.notes && <span className="field-error">{fields.notes}</span>}
          <span className="hint">
            Anything worth knowing about this one. Shows on its own page, and nowhere a customer
            sees.
          </span>
        </label>

        {kind === 'person' &&
          (canLinkAccounts ? (
            <label htmlFor="edit-linkedUserId">
              Linked account
              <select
                id="edit-linkedUserId"
                value={linkedUserId}
                onChange={(e) => setLinkedUserId(e.target.value)}
              >
                <option value="">Not linked</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </option>
                ))}
              </select>
              {fields.linkedUserId && <span className="field-error">{fields.linkedUserId}</span>}
              <span className="hint">
                Linking lets that person see their own assignments and their own record in the app.
                Crew without accounts schedule exactly the same way; they just don’t see it.
              </span>
            </label>
          ) : (
            // Shown rather than hidden: a manager who cannot see this needs to
            // know the link exists and is being left alone, not to wonder
            // whether saving this form has just cut somebody's access.
            <p className="hint">
              {resource.linkedUserId
                ? 'This person is linked to an account. Only an administrator can change that, and saving here leaves it as it is.'
                : 'Only an administrator can link this person to an account.'}
            </p>
          ))}
      </form>
    </Modal>
  );
}
